import React from 'react';
import AdjustableTextarea from '../atoms/AdjustableTextarea';
import { Checkbox, Tabs, Button, Divider } from 'antd';

const WorkflowSection = ({ title, person, date, children }) => (
    <section className="approval-workflow-section">
        <div className="approval-section-header">
            <div className="approval-section-copy">
                <span className="approval-section-kicker">{title}</span>
                {person && <span className="approval-section-person">{person}</span>}
            </div>
            {date && <span className="approval-date-chip">{date}</span>}
        </div>
        {children}
    </section>
);

const ApproveTabs = ({
    complianceCheckController,
    complianceCheckControllerCommentDate,
    complianceCheckComment,
    setComplianceCheckComment,
    complianceCheckDisabled,
    firstApprover,
    firstApproverCommentDate,
    firstApproverComment,
    setFirstApproverComment,
    firstApproverDisabled,
    defaultActiveTab,
}) => {
    const items = [];
    if (complianceCheckController) {
        items.push({
            key: 'complianceCheck',
            label: 'Compliance Check',
            children: (
                <WorkflowSection
                    title="Compliance Check"
                    person={complianceCheckController}
                    date={complianceCheckControllerCommentDate}
                >
                    <AdjustableTextarea
                        value={complianceCheckComment}
                        onChange={setComplianceCheckComment}
                        disabled={complianceCheckDisabled}
                        className="approval-textarea"
                        placeholder="Compliance comment"
                    />
                </WorkflowSection>
            )
        });
    }
    if (firstApprover) {
        items.push({
            key: 'firstApprover',
            label: 'First Approval',
            children: (
                <WorkflowSection
                    title="First Approval"
                    person={firstApprover}
                    date={firstApproverCommentDate}
                >
                    <AdjustableTextarea
                        value={firstApproverComment}
                        onChange={setFirstApproverComment}
                        disabled={firstApproverDisabled}
                        className="approval-textarea"
                        placeholder="First approval comment"
                    />
                </WorkflowSection>
            )
        });
    }

    if (items.length === 0) return null;
    if (items.length === 1) return items[0].children;

    return (
        <div className="approval-tabs">
            <Tabs
                className="approval-workflow-tabs"
                items={items}
                tabBarGutter={16}
                defaultActiveKey={defaultActiveTab}
            />
        </div>
    );
};

const ApproveSubmitForm = ({
    sender,
    senderValue,
    sendingDate,
    approvalDate,
    status,
    onSubmit,
    value,
    onChange,
    firstApproverComment,
    setFirstApproverComment,
    firstApprover,
    firstApproverCommentDate,
    complianceCheckController,
    complianceCheckControllerCommentDate,
    complianceCheckComment,
    setComplianceCheckComment,
    checkBoxValue,
    onCheckBoxChange,
    role,
    onReject,
    onPay,
}) => {
    const disabled =
        status === 'Approved' ||
        status === 'Rejected' ||
        status === 'Pending First Approval' ||
        status === 'Pending Compliance Check' ||
        role === 'Sender or Accountant' ||
        status === 'Paid';

    const buttonDisabled =
        status === 'Approved' ||
        status === 'Rejected' ||
        status === 'Paid' ||
        role === 'Sender or Accountant';

    const firstApproverDisabled = status !== 'Pending First Approval';
    const complianceCheckDisabled = status !== 'Pending Compliance Check';
    const paidButtonDisabled = status !== 'Approved' || role !== 'Admin';

    const shouldDefaultFirstApprover =
        status === 'Pending First Approval' &&
        complianceCheckComment && complianceCheckComment.trim() !== '';

    const defaultActiveTab = shouldDefaultFirstApprover ? 'firstApprover' : 'complianceCheck';

    const handleSubmit = (event) => {
        event.preventDefault();
        onSubmit?.(event);
    };

    return (
        <div className="approval-workflow-form">
            <div className="approval-workflow-heading">
                <h2>Approval Trail</h2>
            </div>

            <WorkflowSection title="Submitted by" person={sender} date={sendingDate}>
                <AdjustableTextarea
                    value={senderValue}
                    disabled
                    className="approval-textarea"
                />
            </WorkflowSection>

            <ApproveTabs
                complianceCheckController={complianceCheckController}
                complianceCheckControllerCommentDate={complianceCheckControllerCommentDate}
                complianceCheckComment={complianceCheckComment}
                setComplianceCheckComment={setComplianceCheckComment}
                complianceCheckDisabled={complianceCheckDisabled}
                firstApprover={firstApprover}
                firstApproverCommentDate={firstApproverCommentDate}
                firstApproverComment={firstApproverComment}
                setFirstApproverComment={setFirstApproverComment}
                firstApproverDisabled={firstApproverDisabled}
                defaultActiveTab={defaultActiveTab}
            />

            <Divider className="approval-divider" />

            <WorkflowSection title="Financial Controller" date={approvalDate}>
                <form className="approval-controller-form" onSubmit={handleSubmit}>
                    <AdjustableTextarea
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                        className="approval-textarea"
                        placeholder="Financial controller comment"
                    />
                    <Checkbox
                        className="approval-checkbox"
                        checked={checkBoxValue}
                        onChange={onCheckBoxChange}
                        disabled={disabled}
                    >
                        Approve with Agreement
                    </Checkbox>
                    <div className="approval-action-row">
                        <Button
                            className="approval-action-button"
                            type="primary"
                            htmlType="submit"
                            disabled={buttonDisabled}
                        >
                            Approve
                        </Button>
                        <Button
                            className="approval-action-button"
                            danger
                            type="button"
                            onClick={onReject}
                            disabled={buttonDisabled}
                        >
                            Reject
                        </Button>
                        <Button
                            className="approval-action-button mark-paid"
                            type="button"
                            onClick={onPay}
                            disabled={paidButtonDisabled}
                        >
                            Mark as Paid
                        </Button>
                    </div>
                </form>
            </WorkflowSection>
        </div>
    );
};

export default ApproveSubmitForm;
